import { useId } from 'react';
import { useT } from '../../../i18n/hooks';
import { Explained } from '../../../components/InfoPopover';
import { FM } from './messages';
import { cx, labelText } from './parts';
import type { FieldProps } from './types';
import './styles';

/**
 * Обёртка для произвольного содержимого: список флажков, составное поле,
 * всё, у чего нет своего поля в React Aria. Подпись, подсказка и ошибка
 * связаны с содержимым через `aria-labelledby` / `aria-describedby`.
 *
 * `as="fieldset"` даёт `<fieldset>` + `<legend>` — для групп переключателей.
 */
export function Field({ as = 'div', label, 'aria-label': ariaLabel, explain, description, errorMessage, isInvalid, isRequired, isDisabled, className, children }: FieldProps) {
  const t = useT();
  const id = useId();
  const labelId = `${id}-l`;
  const hintId = `${id}-h`;
  const errorId = `${id}-e`;
  const showError = Boolean(isInvalid && errorMessage);
  const describedBy = [description ? hintId : null, showError ? errorId : null].filter(Boolean).join(' ') || undefined;
  const caption = label ? (
    <>
      <Explained label={labelText(label, ariaLabel, t(FM.explainFallback))} explain={explain}>
        {label}
      </Explained>
      {isRequired === false ? <span className="fm-opt">{t(FM.optional)}</span> : null}
    </>
  ) : null;
  const body = (
    <>
      <div className="fm-group-body">{children}</div>
      {description ? (
        <div id={hintId} className="fm-hint">
          {description}
        </div>
      ) : null}
      {showError ? (
        <div id={errorId} className="fm-error" role="alert">
          {errorMessage}
        </div>
      ) : null}
    </>
  );
  const common = {
    className: cx('fm-group', 'fm-field', className),
    'data-invalid': isInvalid ? '' : undefined,
    'data-disabled': isDisabled ? '' : undefined,
    'aria-describedby': describedBy,
  };
  if (as === 'fieldset') {
    return (
      <fieldset {...common} disabled={isDisabled} aria-label={label ? undefined : ariaLabel}>
        {caption ? <legend className="fm-legend">{caption}</legend> : null}
        {body}
      </fieldset>
    );
  }
  return (
    <div {...common} role="group" aria-labelledby={label ? labelId : undefined} aria-label={label ? undefined : ariaLabel}>
      {caption ? (
        <span id={labelId} className="fm-legend">
          {caption}
        </span>
      ) : null}
      {body}
    </div>
  );
}
